import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import { printerService } from '../../services/printerService';

import PrinterStatusDashboard from './components/PrinterStatusDashboard';
import ProductionQueuePanel from './components/ProductionQueuePanel';
import QualityControlPanel from './components/QualityControlPanel';
import SupplyManagementPanel from './components/SupplyManagementPanel';
import ProductionStatisticsPanel from './components/ProductionStatisticsPanel';
import MaintenanceSchedulePanel from './components/MaintenanceSchedulePanel';

const PrinterManagementProductionHub = () => {
  const [activeTab, setActiveTab] = useState('queue');
  const [printerStatus, setPrinterStatus] = useState(null);
  const [printQueue, setPrintQueue] = useState([]);
  const [supplyLevels, setSupplyLevels] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [supplyAlerts, setSupplyAlerts] = useState([]);
  const [connectivityAlerts, setConnectivityAlerts] = useState([]);

  useEffect(() => {
    loadPrinterData();
    setupRealtimeSubscriptions();

    // Cleanup subscriptions on unmount
    return () => {
      printerService?.unsubscribeAll();
    };
  }, []);

  const loadPrinterData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [status, queue, supplies, stats] = await Promise.all([
        printerService?.getPrinterStatus(),
        printerService?.getPrintQueue(),
        printerService?.getSupplyLevels(),
        printerService?.getProductionStatistics(
          new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString(),
          new Date()?.toISOString()
        )
      ]);

      setPrinterStatus(status);
      setPrintQueue(queue);
      setSupplyLevels(supplies);
      setStatistics(stats);
    } catch (err) {
      setError(err?.message || 'Failed to load printer data');
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscriptions = () => {
    // Subscribe to print queue updates
    printerService?.subscribeToPrintQueue((job, eventType) => {
      if (eventType === 'INSERT') {
        setPrintQueue(prev => [job, ...prev]);
      } else if (eventType === 'UPDATE') {
        setPrintQueue(prev => 
          prev?.map(item => item?.id === job?.id ? job : item)
        );
      } else if (eventType === 'DELETE') {
        setPrintQueue(prev => 
          prev?.filter(item => item?.id !== job?.id)
        );
      }
    });

    // Subscribe to supply alerts
    printerService?.subscribeToSupplyAlerts((supply, alertInfo) => {
      // Update supply levels
      setSupplyLevels(prev => {
        if (!prev) return prev;
        
        const supplyKey = supply?.supplyType === 'ribbon' ? 'ribbon' :
                         supply?.supplyType === 'card_stock' ? 'cardStock' : 'cleaningKit';
        
        return {
          ...prev,
          [supplyKey]: {
            ...prev?.[supplyKey],
            level: supply?.currentLevel,
            lastUsed: supply?.lastRefilledAt
          }
        };
      });

      // Add alert if supply is low or critical
      if (alertInfo?.isLowSupply || alertInfo?.isCritical) {
        const alertMessage = alertInfo?.isCritical 
          ? `CRITICAL: ${supply?.supplyType} at ${supply?.currentLevel}${supply?.unit} - Immediate reorder required!`
          : `LOW: ${supply?.supplyType} at ${supply?.currentLevel}${supply?.unit} - Reorder recommended`;
        
        setSupplyAlerts(prev => [
          {
            id: supply?.id,
            type: alertInfo?.isCritical ? 'critical' : 'warning',
            message: alertMessage,
            timestamp: new Date()?.toISOString(),
            supplyType: supply?.supplyType
          },
          ...prev?.slice(0, 4) // Keep last 5 alerts
        ]);
      }
    });

    // Subscribe to printer connectivity status
    printerService?.subscribeToConnectivityStatus((status, statusInfo) => {
      // Update printer status
      setPrinterStatus(prev => ({
        ...prev,
        connected: status?.connectionStatus === 'connected',
        status: status?.isReady ? 'ready' : status?.connectionStatus,
        model: status?.printerModel,
        totalPrinted: status?.totalPrintsLifetime,
        errorCount: status?.errorCount,
        lastMaintenance: status?.lastMaintenanceDate,
        nextMaintenance: status?.nextMaintenanceDate
      }));

      // Add connectivity alert if status changed or has errors
      if (statusInfo?.isStatusChange || statusInfo?.hasError) {
        const alertMessage = statusInfo?.isDisconnected
          ? `Printer disconnected - Check connection`
          : statusInfo?.hasError
          ? `Printer error detected - ${status?.errorCount} errors`
          : `Printer status changed: ${statusInfo?.previousStatus} → ${status?.connectionStatus}`;
        
        setConnectivityAlerts(prev => [
          {
            id: status?.id,
            type: statusInfo?.isDisconnected || statusInfo?.hasError ? 'error' : 'info',
            message: alertMessage,
            timestamp: new Date()?.toISOString()
          },
          ...prev?.slice(0, 4) // Keep last 5 alerts
        ]);
      }
    });
  };

  const handleBatchPrint = async (cardIds) => {
    try {
      setLoading(true);
      const result = await printerService?.batchPrint(cardIds);
      alert(`Batch print completed: ${result?.successfulPrints}/${result?.totalCards} cards printed successfully`);
      await loadPrinterData();
    } catch (err) {
      setError(err?.message || 'Batch print failed');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'queue', label: 'Print Queue', icon: 'List' },
    { id: 'quality', label: 'Quality Control', icon: 'CheckCircle' },
    { id: 'supplies', label: 'Supply Management', icon: 'Package' },
    { id: 'statistics', label: 'Production Stats', icon: 'BarChart3' },
    { id: 'maintenance', label: 'Maintenance', icon: 'Settings' }
  ];

  return (
    <>
      <Helmet>
        <title>Printer Management & Production Hub - HR Compliance</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                  <Icon name="Printer" className="w-8 h-8 text-blue-600" />
                  Printer Management & Production Hub
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <span className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></span>
                    Live
                  </span>
                </h1>
                <p className="mt-2 text-gray-600">
                  Real-time Evolis Badgy printer monitoring with live queue updates and supply alerts
                </p>
              </div>
              <button
                onClick={loadPrinterData}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Icon name="RefreshCw" className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* Real-Time Alerts */}
          {(supplyAlerts?.length > 0 || connectivityAlerts?.length > 0) && (
            <div className="mb-6 space-y-3">
              {supplyAlerts?.map(alert => (
                <div 
                  key={alert?.id} 
                  className={`border rounded-lg p-4 flex items-start gap-3 ${
                    alert?.type === 'critical' ?'bg-red-50 border-red-200' :'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <Icon 
                    name="AlertTriangle" 
                    className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                      alert?.type === 'critical' ? 'text-red-600' : 'text-yellow-600'
                    }`} 
                  />
                  <div className="flex-1">
                    <h3 className={`font-semibold ${
                      alert?.type === 'critical' ? 'text-red-900' : 'text-yellow-900'
                    }`}>
                      Supply Alert
                    </h3>
                    <p className={`text-sm ${
                      alert?.type === 'critical' ? 'text-red-700' : 'text-yellow-700'
                    }`}>
                      {alert?.message}
                    </p>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(alert?.timestamp)?.toLocaleTimeString()}
                  </span>
                </div>
              ))}
              {connectivityAlerts?.map(alert => (
                <div 
                  key={alert?.id} 
                  className={`border rounded-lg p-4 flex items-start gap-3 ${
                    alert?.type === 'error' ?'bg-red-50 border-red-200' :'bg-blue-50 border-blue-200'
                  }`}
                >
                  <Icon 
                    name={alert?.type === 'error' ? 'XCircle' : 'Info'} 
                    className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                      alert?.type === 'error' ? 'text-red-600' : 'text-blue-600'
                    }`} 
                  />
                  <div className="flex-1">
                    <h3 className={`font-semibold ${
                      alert?.type === 'error' ? 'text-red-900' : 'text-blue-900'
                    }`}>
                      Connectivity Alert
                    </h3>
                    <p className={`text-sm ${
                      alert?.type === 'error' ? 'text-red-700' : 'text-blue-700'
                    }`}>
                      {alert?.message}
                    </p>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(alert?.timestamp)?.toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900">Notice</h3>
                <p className="text-amber-800 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Printer Status Dashboard */}
          <div className="mb-8">
            <PrinterStatusDashboard 
              status={printerStatus} 
              loading={loading}
              onRefresh={loadPrinterData}
            />
          </div>

          {/* Tab Navigation */}
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                {tabs?.map(tab => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab?.id
                        ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon name={tab?.icon} className="w-4 h-4" />
                    {tab?.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            {activeTab === 'queue' && (
              <ProductionQueuePanel 
                queue={printQueue}
                loading={loading}
                onBatchPrint={handleBatchPrint}
                onRefresh={loadPrinterData}
              />
            )}

            {activeTab === 'quality' && (
              <QualityControlPanel 
                queue={printQueue}
                loading={loading}
              />
            )}

            {activeTab === 'supplies' && (
              <SupplyManagementPanel 
                supplies={supplyLevels}
                loading={loading}
                onUpdate={loadPrinterData}
              />
            )}

            {activeTab === 'statistics' && (
              <ProductionStatisticsPanel 
                statistics={statistics}
                loading={loading}
              />
            )}

            {activeTab === 'maintenance' && (
              <MaintenanceSchedulePanel 
                printerStatus={printerStatus}
                loading={loading}
                onSchedule={loadPrinterData}
              />
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default PrinterManagementProductionHub;
